import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { DialogService } from '../../services/dialog.service';
import { DataService } from '../../services/data.service';
import { OutputGet } from '../../models/output/output-get.model';

@Component({
  selector: 'app-outputs',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule
  ],
  templateUrl: './outputs.component.html',
  styleUrls: ['./outputs.component.scss']
})
export class OutputsComponent implements OnInit {
  @Input() inlineProjectId?: string;
  @Input() inlineMode: boolean = false;
  @Output() backClick = new EventEmitter<void>();
  
  projectId: string | null = null;
  projectName: string = '';
  outputs: OutputGet[] = [];
  loading = false;
  error: string | null = null;
  showBackButton = false;
  
  displayedColumns: string[] = [
    'fileName',
    'fileType',
    'createdOn',
    'actions'
  ];

  constructor(
    private dataService: DataService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private snackBar: MatSnackBar,
    private dialogService: DialogService
  ) {}

  ngOnInit() {
    // Support inline mode (when embedded in project canvas)
    if (this.inlineMode && this.inlineProjectId) {
      this.projectId = this.inlineProjectId;
    } else {
      // Get project ID from route parameters (standalone mode)
      this.projectId = this.route.snapshot.paramMap.get('id');
    }
    
    // Show back button only when viewing a specific project's outputs
    this.showBackButton = !!this.projectId && !this.inlineMode;
    
    // Load outputs - project ID is now optional
    this.loadOutputs();
    
    // Only load project info if we have a project ID and not in inline mode
    if (this.projectId && !this.inlineMode) {
      this.loadProjectInfo();
    } else if (!this.projectId) {
      this.projectName = 'All Projects';
    }
  }

  loadProjectInfo(): void {
    if (!this.projectId) return;
    
    this.dataService.getProject(this.projectId).subscribe({
      next: (project) => {
        this.projectName = project.projectName;
      },
      error: (error: any) => {
        console.error('Error loading project info:', error);
        // Don't set error state, just log it
      }
    });
  }

  loadOutputs(): void {
    this.loading = true;
    this.error = null;

    // Call the FileTracker API to get outputs
    // projectId is now optional - if not provided, gets all outputs
    this.dataService.getOutputList(this.projectId || undefined).subscribe({
      next: (outputs: OutputGet[]) => {
        this.outputs = outputs;
        this.loading = false;
      },
      error: (error: any) => {
        console.error('Error loading outputs:', error);
        this.error = 'Failed to load outputs. Please try again.';
        this.loading = false;
        this.snackBar.open(error.message || 'Failed to load outputs', '×', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
      }
    });
  }

  goBack(): void {
    if (this.inlineMode) {
      // In inline mode, emit event for parent component to handle
      this.backClick.emit();
    } else {
      // In standalone mode, use browser back
      this.location.back();
    }
  }

  downloadOutput(output: OutputGet): void {
    if (!output.fileName || !output.projectId || !output.blobPath) {
      console.error('File name, project ID, or blob path is missing');
      return;
    }

    // Call the FileTracker API to download the file
    this.dataService.downloadOutput(output.projectId, output.fileName, output.blobPath).subscribe({
      next: (response) => {
        // Decode base64 content and trigger download
        const byteCharacters = atob(response.contentBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: response.contentType });
        
        // Create a download link and trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      error: (error: any) => {
        console.error('Error downloading output:', error);
        this.snackBar.open('Failed to download output. Please try again.', '×', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
      }
    });
  }

  onDeleteOutput(event: Event, output: OutputGet): void {
    event.stopPropagation();

    const projectId = output.projectId || this.projectId || '';
    const fileName = output.fileName || output.id || '';
    const blobPath = output.blobPath || '';
    const name = output.fileName || 'Output';

    if (!projectId || !fileName || !blobPath) {
      this.snackBar.open('Unable to determine project ID, file name, or blob path to delete', '×', { duration: 4000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar'] });
      return;
    }

    this.dialogService.openConfirmDialog(
      'Delete Output',
      `Are you sure you want to delete "${name}"? This action cannot be undone.`,
      'Delete',
      'Cancel'
    ).subscribe(confirmed => {
      if (!confirmed) return;

      this.dataService.deleteCleanResult(projectId, fileName, blobPath).subscribe({
        next: (success: boolean) => {
          if (success) {
            this.loadOutputs();
          } else {
            this.snackBar.open('Failed to delete output', '×', { duration: 5000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar'] });
          }
        },
        error: (error: any) => {
          console.error('Error deleting output:', error);
          this.snackBar.open(error.message || 'Failed to delete output', '×', { duration: 5000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar'] });
        }
      });
    });
  }

  formatFileSize(bytes?: number): string {
    if (!bytes) return 'N/A';
    
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  }

  /**
   * Infer file type from filename extension
   */
  getFileType(filename: string): string {
    if (!filename) return 'Unknown';
    
    const extension = filename.split('.').pop()?.toLowerCase();
    
    switch (extension) {
      case 'csv': return 'CSV';
      case 'xlsx':
      case 'xls': return 'Excel';
      case 'json': return 'JSON';
      case 'parquet': return 'Parquet';
      case 'pdf': return 'PDF';
      case 'xml': return 'XML';
      case 'txt': return 'Text';
      case 'zip':
      case 'gz':
      case 'tar': return 'Archive';
      case 'html': return 'HTML';
      case 'avro': return 'Avro';
      default: return extension ? extension.toUpperCase() : 'Unknown';
    }
  }

  getFileTypeIcon(filename: string): string {
    const fileType = this.getFileType(filename).toLowerCase();
    
    if (fileType.includes('csv')) return 'chart';
    if (fileType.includes('excel') || fileType.includes('xlsx') || fileType.includes('xls')) return 'microsoft-excel';
    if (fileType.includes('json')) return 'json';
    if (fileType.includes('parquet')) return 'database';
    if (fileType.includes('pdf')) return 'picture_as_pdf';
    if (fileType.includes('archive') || fileType.includes('zip')) return 'folder_zip';
    if (fileType.includes('xml') || fileType.includes('html')) return 'code';
    if (fileType.includes('text') || fileType.includes('txt')) return 'docs';
    
    return 'insert_drive_file';
  }

  /**
   * Check if the icon is a custom SVG icon
   */
  isCustomIcon(iconName: string): boolean {
    const customIcons = ['chart', 'microsoft-excel', 'database', 'docs', 'json'];
    return customIcons.includes(iconName);
  }
}
